import React, { useContext } from "react";
import Tooltip from "@mui/material/Tooltip";
import { TranscriptLineViewModel } from "./TranscriptViewModel";
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import Typography from "@mui/material/Typography";
import { APIContext } from "../../Contexts/APIContext";
import TranscriptTextFinder from "./TranscriptTextFinder";
import { removePunctuation } from "../../Utilities/UtilityMethods";
import TranscriptSearch from "./TranscriptSearch";

const styles = {
    sentimentGood: {
        marginRight: '4px',
        marginBottom: '-5px',
        color: `rgba(0,110,16, 1 )`
    },
    sentimentGoodFade: {
        marginRight: '4px',
        marginBottom: '-5px',
        color: `rgba(0,110,16, 0.4)`
    },
    sentimentNeutral: {
        marginRight: '4px',
        marginBottom: '-5px',
        color: `rgba(0,0,255, 1)`
    },
    sentimentNeutralFade: {
        marginRight: '4px',
        marginBottom: '-5px',
        color: `rgba(0,0,255, 0.4)`
    },
    sentimentBad: {
        marginRight: '4px',
        marginBottom: '-5px',
        color: `rgba(255, 0,0, 1 )`
    },
    sentimentBadFade: {
        marginRight: '4px',
        marginBottom: '-5px',
        color: `rgba(255, 0,0, 0.4)`
    },
    hover: {
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline'
        }
    },
    lowConfidenceWord: {
        fontStyle: 'italic',
        color: "red",
    },
    lowConfidenceWordSelectedHighlight: {
        fontStyle: 'italic',
        color: "red",
        backgroundColor: "yellow",
    },
    lowConfidenceWordHighlight: {
        fontStyle: 'italic',
        color: "red",
        backgroundColor: "#e9851c"
    },
    selectedHighlight: {
        backgroundColor: "yellow",
    },
    highlight: {
        backgroundColor: "#e9851c",
    },
    noHighlight: {
        backgroundColor: "transparent",
    },
}

interface ITranscriptLineProps {
    line: TranscriptLineViewModel;
    channel: string;
    transcriptSearch?: TranscriptSearch;
    textFinderViewModel?: TranscriptTextFinder;
    lineIndex: number;
    messageIndex: number;
}


export default function TranscriptLine(props: ITranscriptLineProps) {
    const { wavesurfer } = useContext(APIContext);

    const jumpTo = (time) => () => {
        wavesurfer!.current?.setTime(time);
    };

    // Used to find the indices of all sets of words that that match the transcript search filter
    var allMatchingIndices: number[] = [];
    var currentMatchingIndices: number[] = [];

    let formattedSearchText;
    let formattedSimilarWords;

    if (props.transcriptSearch?.mode) {
        formattedSearchText = props.transcriptSearch!.searchText
            .split(' ')
            .map(x => removePunctuation(x.trim().toLowerCase()));

        formattedSimilarWords = props.transcriptSearch!.similarWords
            .map(x => removePunctuation(x.trim().toLowerCase()));
    }

    const formattedLineText = props.line.words
        .map(x => removePunctuation(x.content.trim().toLowerCase()));

    // Finds indices of any words that match any of the words in the search text
    const anySearch = () => {
        for (let i: number = 0; i < formattedLineText.length; i++) {
            if (formattedSearchText.includes(formattedLineText[i])) {
                allMatchingIndices.push(i);
            }
        }
    }

    // Finds indices of sets of words that match all of the words in the search text
    const allSearch = () => {
        var searchTextIndex = 0;
        for (let i: number = 0; i < formattedLineText.length; i++) {
            if (formattedLineText[i] === formattedSearchText[searchTextIndex]) {
                currentMatchingIndices.push(i);
                searchTextIndex++;
                // if match is already found, resets to find another match on same line
                if (currentMatchingIndices.length === formattedSearchText.length) {
                    currentMatchingIndices.forEach(x => allMatchingIndices.push(x));
                    currentMatchingIndices = [];
                    searchTextIndex = 0;
                }
            }
            else {
                currentMatchingIndices = [];
                searchTextIndex = 0;
            }
        }
    }

    // Finds indices of any words that are similar any of the words in the search text
    const similarSearch = () => {
        for (let i: number = 0; i < formattedLineText.length; i++) {
            if (formattedSearchText.includes(formattedLineText[i]) || formattedSimilarWords.includes(formattedLineText[i])) {
                allMatchingIndices.push(i);
            }
        }
    }

    if (props.transcriptSearch) {
        if (props.channel === props.transcriptSearch.speakerChannel) {
            if (props.transcriptSearch.mode === "Any" || props.transcriptSearch.mode === "All") {
                anySearch();
            }
            if (props.transcriptSearch.mode === "Phrase") {
                allSearch();
            }
            if (props.transcriptSearch.mode === "Similar") {
                similarSearch();
            }
        }
    }

    const isTextSearchMatch = (wordIndex: number) => {
        if (props.textFinderViewModel) {
            const message = props.textFinderViewModel.messages.find(message => message.index === props.messageIndex);
            if (message) {
                const line = message.lines.find(line => line.lineIndex === props.lineIndex);
                if (line) {
                    for (const setOfIndices of line.matchingSetsOfIndices) {
                        if (setOfIndices.includes(wordIndex)) {
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    }

    const generateClasses = (wordIndex: number) => {
        let classNames = '';
        if (props.textFinderViewModel?.matchCount) {
            if (isTextSearchMatch(wordIndex)) {
                classNames += 'Text-Match';
                let index = 0;
                for (const message of props.textFinderViewModel.messages) {
                    for (const line of message.lines) {
                        for (const matchingIndices of line.matchingSetsOfIndices) {
                            if (message.index === props.messageIndex && line.lineIndex === props.lineIndex) {
                                if (matchingIndices.includes(wordIndex)) {
                                    classNames += ` Text-Match-${index}`
                                    return classNames;
                                }
                            }
                            index++;
                        }
                    }
                }
            }
        }
        return classNames;
    }

    return (
        <Tooltip title={props.line.time} placement="left" >
            <Typography variant="body2" onClick={jumpTo(props.line.timeSeconds)} sx={styles.hover}>
                {props.line.sentiment !== "" ?
                    <Tooltip title={
                        props.line.emotion ?
                            `Emotion: ${props.line.emotion} - Score: ${props.line.score}`
                            : "No sentiment found"
                    }
                        placement="top">
                        {props.line.sentiment == "positive" ?
                            <SentimentVerySatisfiedIcon fontSize="small" sx={{
                                marginRight: '4px', marginBottom: '-5px', color: `rgba(0, 110, 16, ${props.line.alphaScale})`
                            }} />
                            : props.line.sentiment == "neutral" ?
                                <SentimentNeutralIcon fontSize="small" sx={{ marginRight: '4px', marginBottom: '-5px', color: `rgba(0, 0, 255, ${props.line.alphaScale})` }} />
                                : <SentimentVeryDissatisfiedIcon fontSize="small" sx={{ marginRight: '4px', marginBottom: '-5px', color: `rgba(255, 0, 0, ${props.line.alphaScale})` }} />
                        }
                    </Tooltip>
                    : <></>
                }
                {
                    props.line.words.map((word, wordIndex) => 
                        <Tooltip title={`${(word.confidence * 100).toFixed(0)}%`} placement="top" key={wordIndex}>
                            <span
                                className={generateClasses(wordIndex)}>
                                {word.content}
                            </span>
                        </Tooltip>
                    )
                }
            </Typography >
        </Tooltip >
    );
}